import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box, useTheme} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine} from '@dropbox/dig-icons/assets';
import {GlobalHeader} from '@dropbox/dpc-ia';
import DropboxOSIcon from 'assets/DropboxOSIcon.svg';
import DropboxOSIconDark from 'assets/DropboxOSIconDark.svg';
import DropboxOSIconDarkMobile from 'assets/DropboxOSIconDarkMobile.svg';
import DropboxOSIconMobile from 'assets/DropboxOSIconMobile.svg';
import {pulseUserAtom} from 'atoms/auth';
import {isMobileAtom} from 'atoms/layout';
import {Link} from 'components/DSYS/Link';
import {isDev, isProd} from 'helpers/environment';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useCallback, useEffect, useState} from 'react';

import styles from './DocumentsTable.module.css';
import {DocumentStatusChip, DocumentStatusChipContent} from './DocumentStatusChip';
import {DocumentStatus, TopNavDocument} from './types';
import {
  canEditDocument,
  getStatusBorder,
  getStatusColor,
  getStatusIconSrc,
  getStatusText,
} from './utils';

type DocumentTopNavProps = {
  doc: TopNavDocument | undefined;
  statusMenu?: {
    menuStatuses: DocumentStatus[];
    onSelectStatus?: (status: DocumentStatus) => void;
  };
  navMenu?: React.ReactNode;
  isError?: boolean;
};

export const DocumentTopNav = (props: DocumentTopNavProps) => {
  const {navMenu} = props;
  return (
    <DocumentNavWrapper {...props}>
      <Box className={styles.navActions}>
        <Box display="flex" alignItems="center" justifyContent="flex-end" columnGap="8">
          {navMenu}
        </Box>
      </Box>
    </DocumentNavWrapper>
  );
};

export const DocumentTopNavPlaceholder = (props: Exclude<DocumentTopNavProps, 'navMenu'>) => {
  return (
    <DocumentNavWrapper {...props}>
      {props.isError ? null : (
        <Box className={styles.navActions}>
          <Skeleton.Box width={200} height={32} />
        </Box>
      )}
    </DocumentNavWrapper>
  );
};

const DocumentNavWrapper = ({
  doc,
  statusMenu,
  children,
}: DocumentTopNavProps & {children: React.ReactNode}) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const canEdit = canEditDocument(pulseUser, doc?.owner.ldap);
  const docName = doc?.name || t('untitled');
  const {mode} = useTheme();
  const isMobile = useAtomValue(isMobileAtom);

  return (
    <GlobalHeader.Wrapper style={{boxSizing: 'border-box', height: 52, paddingRight: '12px'}}>
      <Link to="/" style={{height: 35}} state={{source: 'navbar'}}>
        <img
          src={
            isMobile
              ? mode === 'dark'
                ? DropboxOSIconDarkMobile
                : DropboxOSIconMobile
              : mode === 'dark'
                ? DropboxOSIconDark
                : DropboxOSIcon
          }
          style={{
            filter: isDev ? 'grayscale(1)' : !isProd ? 'hue-rotate(127deg)' : '',
          }}
        />
      </Link>
      <Box className={styles.navTitleSection}>
        {doc && (
          <>
            <Text isBold className={styles.navName}>
              <Truncate lines={1} tooltipControlProps={{placement: 'bottom'}}>
                {docName}
              </Truncate>
            </Text>

            {!!statusMenu?.menuStatuses?.length && (
              <Box className={styles.navStatus}>
                <DocumentStatusMenu
                  documentStatus={doc.status}
                  menuStatuses={statusMenu.menuStatuses}
                  canEdit={canEdit}
                  onSelectStatus={statusMenu?.onSelectStatus}
                />
              </Box>
            )}
          </>
        )}
      </Box>
      {children}
    </GlobalHeader.Wrapper>
  );
};

export const DocumentStatusMenu = ({
  documentStatus,
  menuStatuses,
  canEdit,
  onSelectStatus,
  isPortaled = false,
}: {
  documentStatus: DocumentStatus;
  menuStatuses: DocumentStatus[];
  canEdit: boolean;
  onSelectStatus?: (status: DocumentStatus) => void;
  isPortaled?: boolean;
}) => {
  const [selectedStatus, setSelectedStatus] = useState(documentStatus);
  const statusIconSrc = getStatusIconSrc(selectedStatus);

  const handleStatusSelection = useCallback((newStatus: DocumentStatus) => {
    setSelectedStatus(newStatus);
  }, []);

  // Update selected status to document's status whenever it changes
  useEffect(() => {
    setSelectedStatus(documentStatus);
  }, [documentStatus]);

  if (!canEdit || selectedStatus === DocumentStatus.COMPLETED) {
    return <DocumentStatusChip status={selectedStatus} statusIconSrc={statusIconSrc} />;
  }

  return (
    <Menu.Wrapper onSelection={handleStatusSelection} isPortaled={isPortaled}>
      {({getTriggerProps, getContentProps, closeMenu}) => {
        return (
          <>
            <Chip
              {...getTriggerProps()}
              size="small"
              disabled={false}
              style={{
                backgroundColor: getStatusColor(selectedStatus),
                border: getStatusBorder(selectedStatus),
              }}
              withDropdownIcon
            >
              <DocumentStatusChipContent
                status={selectedStatus}
                statusIconSrc={statusIconSrc}
                withDropdownSpace
              />
            </Chip>

            {menuStatuses.length > 0 && (
              <Menu.Content {...getContentProps()} style={{width: 200, zIndex: 1000}}>
                <Menu.Segment>
                  {menuStatuses.map((status) => (
                    <Menu.Row
                      key={`document_status__${status}`}
                      onClick={() => {
                        onSelectStatus?.(status);
                        closeMenu({});
                      }}
                      withRightAccessory={
                        selectedStatus === status ? (
                          <UIIcon size="medium" src={CheckmarkLine} />
                        ) : null
                      }
                      interactive
                    >
                      <Text size="medium">{getStatusText(status)}</Text>
                    </Menu.Row>
                  ))}
                </Menu.Segment>
              </Menu.Content>
            )}
          </>
        );
      }}
    </Menu.Wrapper>
  );
};
